import { noop } from 'shared/utils/function';
import { MergedCanvasResult } from './types';
import { MIME_TYPES } from 'shared/constants/MIME_TYPES';
import { blobLifecycleService } from 'services/fileOperations';

export const MAX_DRAW_CANVAS_DIMENSION = 8192;

export class CanvasService {
  capDrawDimensions(
    width: number,
    height: number,
    maxDimension: number = MAX_DRAW_CANVAS_DIMENSION
  ): { width: number; height: number; scale: number } {
    if (!width || !height) {
      return { width, height, scale: 1 };
    }

    const largestSide = Math.max(width, height);
    if (largestSide <= maxDimension) {
      return { width, height, scale: 1 };
    }

    const scale = maxDimension / largestSide;
    return {
      width: Math.max(1, Math.round(width * scale)),
      height: Math.max(1, Math.round(height * scale)),
      scale,
    };
  }

  prepareDrawDisplaySource(
    source: string,
    naturalWidth: number,
    naturalHeight: number,
    maxDimension: number = MAX_DRAW_CANVAS_DIMENSION,
    loadedImage?: HTMLImageElement
  ): Promise<{ source: string; width: number; height: number; cleanup?: () => void }> {
    const capped = this.capDrawDimensions(
      naturalWidth,
      naturalHeight,
      maxDimension
    );

    if (capped.scale === 1) {
      return Promise.resolve({
        source,
        width: naturalWidth,
        height: naturalHeight,
      });
    }

    const imagePromise = loadedImage
      ? Promise.resolve(loadedImage)
      : this.loadImageFromSource(source);

    return imagePromise.then(async (image) => {
      const canvas = document.createElement('canvas');
      canvas.width = capped.width;
      canvas.height = capped.height;
      const context = canvas.getContext('2d');
      if (!context) {
        this.releaseImageElement(image);
        return {
          source,
          width: naturalWidth,
          height: naturalHeight,
        };
      }

      context.drawImage(image, 0, 0, capped.width, capped.height);
      this.releaseImageElement(image);

      const blob = await this.canvasToBlob(canvas);
      if (!blob) {
        return {
          source,
          width: naturalWidth,
          height: naturalHeight,
        };
      }

      const transientBlob = blobLifecycleService.createTransientBlobUrl(blob);

      return {
        source: transientBlob.url,
        width: capped.width,
        height: capped.height,
        cleanup: transientBlob.cleanup,
      };
    });
  }
  loadImageFromSource(source: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'use-credentials';
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Could not load image'));
      image.src = source;
    });
  }

  releaseImageElement(image: HTMLImageElement): void {
    image.onload = null;
    image.onerror = null;
    image.src = '';
  }

  canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
    return new Promise((resolve) => canvas.toBlob(resolve));
  }

  toPersistedImageSource(
    canvas: HTMLCanvasElement,
    blob: Blob | null
  ): string {
    if (blob) {
      return blobLifecycleService.createPersistentBlobUrl(blob);
    }

    try {
      const dataUrl = canvas.toDataURL();
      if (dataUrl && dataUrl.startsWith('data:')) {
        return dataUrl;
      }
    } catch {
      noop();
    }

    return canvas.toDataURL(MIME_TYPES.IMAGE_PNG);
  }

  createMergedImageCanvas(
    image: HTMLImageElement,
    drawing: HTMLCanvasElement
  ): MergedCanvasResult {
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    const context = canvas.getContext('2d')!;

    context.drawImage(image, 0, 0, image.width, image.height);
    context.drawImage(drawing, 0, 0, image.width, image.height);

    const thumbnailCanvas = this.createThumbnailImageCanvas(image, drawing);

    return { original: canvas, thumbnail: thumbnailCanvas };
  }

  createThumbnailImageCanvas(
    image: HTMLImageElement,
    drawing: HTMLCanvasElement
  ): HTMLCanvasElement {
    const MAX_WIDTH = 300;
    const MAX_HEIGHT = 300;

    let width = image.width;
    let height = image.height;

    if (width > height) {
      if (width > MAX_WIDTH) {
        height = height * (MAX_WIDTH / width);
        width = MAX_WIDTH;
      }
    } else if (height > MAX_HEIGHT) {
      width = width * (MAX_HEIGHT / height);
      height = MAX_HEIGHT;
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d')!;
    context.drawImage(image, 0, 0, width, height);
    context.drawImage(drawing, 0, 0, width, height);
    return canvas;
  }
}

export const canvasService = new CanvasService();

export type { MergedCanvasResult } from './types';
